import React, { useState } from 'react';

import { useManagementPackContext } from '../ManagementPackContext';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

const Alternative = ({ heading, text, children }) => (
    <div className="tw-flex tw-flex-col tw-gap-1 tw-border-0 tw-border-t tw-border-solid tw-border-charcoal-15 tw-pt-3">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-charcoal-75">{heading}</span>
        <p className="tw-text-sm tw-text-charcoal tw-whitespace-pre-line tw-m-0">{text}</p>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">{children}</div>
    </div>
);

/**
 * The draft commentary actions for the page shown, under the slide: accept a
 * draft in the box or use the last report's text instead, use or dismiss a
 * new draft offered beside settled text, and regenerate the page's draft.
 */
const CommentaryDraftPanel = ({ commentary }) => {
    const { regenerateDraft, chooseDraft, dismissDraft } = useManagementPackContext();
    const [busy, setBusy] = useState(false);

    const run = (action) => {
        setBusy(true);
        Promise.resolve(action()).finally(() => setBusy(false));
    };

    const { pageKey, state, draft, carriedForward, draftable } = commentary;
    const stale = commentary.stale || !!draft?.stale;

    const regenerate = draftable && (
        <TextButton onClick={() => run(() => regenerateDraft(pageKey))} disabled={busy}>
            {$t('Regenerate draft')}
        </TextButton>
    );

    return (
        <div className="tw-flex tw-flex-col tw-gap-3 tw-bg-white tw-p-4 tw-shadow-primary" aria-label={$t('Draft commentary')} role="region">
            {state === 'draft' && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-flex-col">
                        <span className="tw-text-sm tw-text-charcoal-75">
                            {$t("Draft written from this page's figures on {{date}}", { date: draft?.generatedAtLabel || '' })}
                        </span>
                        {stale && <span className="tw-text-sm tw-text-amber-700">{$t('Stale: figures have changed')}</span>}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        {regenerate}
                        <Button variant="primary" small onClick={() => run(() => chooseDraft(pageKey, 'draft'))} disabled={busy}>
                            {$t('Accept')}
                        </Button>
                    </div>
                </div>
            )}

            {state !== 'draft' && stale && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <span className="tw-text-sm tw-text-amber-700">{$t('Stale: figures have changed')}</span>
                    {regenerate}
                </div>
            )}

            {state === 'text' && !stale && regenerate && <div className="tw-flex tw-justify-end">{regenerate}</div>}

            {state === 'draft' && carriedForward && (
                <Alternative heading={$t('From the last report ({{period}})', { period: carriedForward.periodLabel || '' })} text={carriedForward.text}>
                    <TextButton onClick={() => run(() => chooseDraft(pageKey, 'carried_forward'))} disabled={busy}>
                        {$t('Use this instead')}
                    </TextButton>
                </Alternative>
            )}

            {state === 'offer' && draft && (
                <Alternative heading={$t('New draft ({{date}})', { date: draft.generatedAtLabel || '' })} text={draft.text}>
                    <TextButton variant="primary" onClick={() => run(() => chooseDraft(pageKey, 'draft'))} disabled={busy}>
                        {$t('Use new draft')}
                    </TextButton>
                    <TextButton onClick={() => run(() => dismissDraft(pageKey))} disabled={busy}>
                        {$t('Dismiss')}
                    </TextButton>
                </Alternative>
            )}
        </div>
    );
};

export default CommentaryDraftPanel;
